import React from 'react';
import { ArrowRight, Zap, CheckCircle, Users, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const stats = [
    { icon: Building, label: 'Year Established', value: '1991' },
    { icon: Zap, label: 'Years Experience in Electrical', value: '35' },
    { icon: Users, label: 'Government and Private Clients', value: '50+' }
  ];

  const services = [
    {
      icon: Zap,
      title: 'Electrical',
      description: 'Complete electrical services including management systems, distribution systems, switchgears, CCTV, fire alarms, and substations up to 132 KV.',
      image: 'https://images.unsplash.com/photo-1759692071712-adc78a8516c8?w=400&q=70&auto=format'
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1487491424367-7571f9afbb30?w=1200&q=75&auto=format"
            alt="Engineering construction site"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Delivering Electrical and<br />Integrated Engineering Solutions
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-slate-200 max-w-3xl mx-auto">
            Across India for 35 years - Your trusted partner in electrical engineering
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
              onClick={() => scrollToSection('services')}
            >
              Our Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 px-8 py-6 text-lg"
              onClick={() => scrollToSection('contact')}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-8 text-center">
                    <Icon className="h-12 w-12 mx-auto mb-4 text-blue-600" />
                    <div className="text-4xl font-bold text-slate-900 mb-2">{stat.value}</div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-slate-900">About Hi-Tech Engineers</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Hi-Tech Engineers and Consultants is a complete electrical engineering solutions company. Operating for the last 35 years across India, we provide services to the government, private and corporate sectors.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Our Vision</h3>
                    <p className="text-slate-600">To become our customer's first choice at providing electrical & engineering solutions.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Quality Commitment</h3>
                    <p className="text-slate-600">100% client satisfaction through excellence in delivery and service.</p>
                  </div>
                </div>
              </div>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => scrollToSection('about')}
              >
                Learn More About Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1685946973834-81f1c465ec0c?w=600&q=75&auto=format"
                alt="Modern office environment"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-xl text-slate-300">Comprehensive electrical engineering solutions tailored to your needs</p>
          </div>
          
          <div className="max-w-md mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="bg-slate-800 border-slate-700 overflow-hidden hover:transform hover:scale-105 transition-transform duration-300">
                  <div className="h-48 overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <CardContent className="p-6">
                    <Icon className="h-10 w-10 mb-4 text-blue-400" />
                    <h3 className="text-2xl font-bold mb-3 text-white">{service.title}</h3>
                    <p className="text-slate-300 mb-4">{service.description}</p>
                    <Button 
                      variant="outline" 
                      className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                      onClick={() => scrollToSection('services')}
                    >
                      View Details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Highlights Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Project Highlights</h2>
            <p className="text-xl text-slate-600">Excellence in execution and delivery</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">50+</div>
                <p className="text-lg text-slate-600 mb-4">Projects Completed</p>
                <p className="text-sm text-slate-500">Successfully delivered across India</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">100%</div>
                <p className="text-lg text-slate-600 mb-4">On-Time Delivery</p>
                <p className="text-sm text-slate-500">Committed to project timelines</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">97%</div>
                <p className="text-lg text-slate-600 mb-4">Client Retention</p>
                <p className="text-sm text-slate-500">Long-term partnerships built on trust</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;